import { LitElement, html, css } from 'lit';
import { API_BASE_URL } from './utils.js';

export class ProductsTab extends LitElement {
    static properties = {
        contextManager: { type: Object },
        activeContextId: { type: String },
        products: { state: true },
        filteredProducts: { state: true },
        isLoading: { state: true },
        searchTerm: { state: true },
        contentUrl: { state: true },
    };

    static styles = css`
        :host {
            display: block;
            padding: 20px;
        }

        .container {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .search-bar {
            margin-bottom: 16px;
        }

        sp-table {
            width: 100%;
            max-width: 100%;
        }

        .loading-hidden {
            display: none;
        }

        .no-context-message {
            text-align: center;
            padding: 40px;
            background-color: var(--spectrum-gray-100);
            border-radius: 8px;
            border: 1px solid var(--spectrum-gray-300);
        }

        #product-count {
            margin-bottom: 5px;
        }

        #pagination {
            margin-top: 5px;
        }
        
    `;

    constructor() {
        super();
        this.contextManager = null;
        this.activeContextId = null;
        this.products = [];
        this.filteredProducts = [];
        this.isLoading = false;
        this.searchTerm = '';
        this.contentUrl = '';
    }

    updated(changedProperties) {
        if (changedProperties.has('activeContextId') && this.activeContextId) {
            this.fetchProducts(0, 1000);
            this.loadContentUrl();
        }
    }

    loadContentUrl() {
        const credentials = this.contextManager?.getContextCredentials();
        let defaultContentUrl = '';
        if (credentials && credentials.org && credentials.site) {
            defaultContentUrl = `https://main--${credentials.site}--${credentials.org}.aem.live`;
        }

        const storedParams = localStorage.getItem(`scrapingParams_${this.activeContextId}`);
        if (storedParams) {
            const params = JSON.parse(storedParams);
            this.contentUrl = params.contentUrl || defaultContentUrl;
        } else {
            this.contentUrl = defaultContentUrl;
        }
    }

    async fetchProducts(page = 0, limit = 1000) {        
        const credentials = this.contextManager?.getContextCredentials();
        if (!credentials || !credentials.org || !credentials.site) {
            console.error('No active context credentials or missing org/site');
            this.products = [];
            this.filteredProducts = [];
            return;
        }
        const offset = page * limit;

        const { org, site, siteToken } = credentials;

        let headers = {};
        if (siteToken) {
            headers = {'x-site-token':siteToken };
        }


        const url = `${API_BASE_URL}/published-products-index?org=${org}&site=${site}&offset=${offset}&limit=${limit}`;

        this.isLoading = true;
        try {
            const response = await fetch(url, {
                headers
            });
            if (response.ok) {
                const data = await response.json();
                this.products = data.data;
                this.total = data.total;
                this.offset = data.offset;
                this.filterProducts();
            } else {
                console.error('Error fetching products:', response.statusText);
                this.products = [];
                this.filteredProducts = [];
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            this.products = [];
            this.filteredProducts = [];
        } finally {
            this.isLoading = false;
        }
    }

    handleSearch(event) {
        event.preventDefault();
        event.stopPropagation();
        this.searchTerm = event.target.value.toLowerCase();
        this.filterProducts();
    }

    handlePagination(event) {
        if(event.key === 'Enter') {
            const page = parseInt(event.target.value);
            if (Number.isInteger(page)) {
                if (page >= 1 && page <= this.totalPages) {
                    this.fetchProducts((page - 1), 1000);
                }
            }
        }
    }

    filterProducts() {
        if (!this.searchTerm) {
            this.filteredProducts = this.products;
            return;
        }

        this.filteredProducts = this.products.filter(product =>
            Object.values(product).some(value =>
                String(value).toLowerCase().includes(this.searchTerm)
            )
        );
    }
    
    formatDate(timestamp) {
        if (!timestamp) return 'N/A';
        // timestamp is in seconds, convert to milliseconds
        return new Date(timestamp * 1000).toLocaleString();
    }

    render() {
        this.limit = 1000;
        const startingIndex = this.offset + 1;
        const endingIndex = this.offset + this.filteredProducts.length;
        this.totalPages = Math.ceil(this.total / this.limit);

        const productCount =`Showing ${startingIndex} - ${endingIndex} of ${this.total} products`;        

        const pagination =html`Page: 
                    <input id="page-input"
                        type="number" min="1" max="${this.totalPages}" value="1"
                        @keyup=${this.handlePagination}
                    />                                    
                of ${this.totalPages}`;

        if (!this.activeContextId) {
            return html`
                <div class="no-context-message">
                    <p>No active context selected. Please select or create a context in the Settings tab.</p>
                </div>
            `;
        }

        return html`
            <div class="container">
                <h3 id="product-count">${Number.isFinite(startingIndex) ? productCount : ''}</h3>
                <h3 id="pagination">${Number.isFinite(this.totalPages) ? pagination : ''}</h3>
               
                <div class="search-bar">
                    <sp-search
                        @input=${this.handleSearch}
                        placeholder="Search products..."
                    ></sp-search>
                </div>

                <sp-progress-circle .indeterminate=${this.isLoading} label="Loading products..." class="${this.isLoading ? '' : 'loading-hidden'}"></sp-progress-circle>
                
                ${!this.isLoading ? html`
                    <sp-table>
                        <sp-table-head>
                            <sp-table-head-cell>Path</sp-table-head-cell>
                            <sp-table-head-cell>SKU</sp-table-head-cell>
                            <sp-table-head-cell>Last Modified</sp-table-head-cell>
                        </sp-table-head>
                        <sp-table-body>
                            ${this.filteredProducts.map(product => html`
                                <sp-table-row>
                                    <sp-table-cell>
                                        <a href="${this.contentUrl.replace(/\/$/, '')}/${product.path.replace(/^\//, '')}" target="_blank">
                                            ${product.path}
                                        </a>
                                    </sp-table-cell>
                                    <sp-table-cell>${product.sku}</sp-table-cell>
                                    <sp-table-cell>${this.formatDate(product['last-modified'])}</sp-table-cell>
                                </sp-table-row>
                            `)}
                        </sp-table-body>
                    </sp-table>
                ` : ''}
            </div>
        `;
    }
}

customElements.define('products-tab', ProductsTab); 